import React, { useState, useRef, useEffect } from 'react'
import { ArrowUp } from 'lucide-react'
import { useUserStore } from '@/stores/userStore'
import { generateUUIDFromTimestamp } from '@/lib/utils'
import AIAvatarPlain from '@/assets/images/chat/avatar_ai_plain.png'
import type { AgentRes } from '@/api/types/agent.type'
import Icon from '@/components/Icon/Icon'

interface WelcomePageProps {
  onStartChat: (sessionId: string, userMessage: string) => Promise<void>
  agentDetail?: AgentRes // 新增智能体详情参数
  isSearchConfigReady?: boolean // 知识库配置是否准备就绪
}

const WelcomePage: React.FC<WelcomePageProps> = ({
  onStartChat,
  agentDetail,
  isSearchConfigReady = true,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [isSending, setIsSending] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { currentUser } = useUserStore()

  // 获取用户ID
  const userId = currentUser?.id?.toString() || '123456'

  // 自动调整textarea高度
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px'
    }
  }, [inputValue])

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isSending || !isSearchConfigReady) return

    try {
      setIsSending(true)

      // 1. 使用generateUUIDFromTimestamp生成新的sessionId
      const timestamp = Date.now()
      const newSessionId = generateUUIDFromTimestamp(timestamp, userId)

      // 2. 通知父组件开始聊天（这会跳转到会话页面）
      await onStartChat(newSessionId, inputValue)

      // 3. 清空输入框
      setInputValue('')
      setIsSending(false)

      // 注意：消息发送由ChatSession组件处理
    } catch (error) {
      console.error('创建会话失败:', error)
      setIsSending(false)
    }
  }

  // 处理提示问题点击
  const handleSuggestionClick = async (suggestion: string) => {
    if (isSending || !isSearchConfigReady) return

    try {
      setIsSending(true)

      // 1. 使用generateUUIDFromTimestamp生成新的sessionId
      const timestamp = Date.now()
      const newSessionId = generateUUIDFromTimestamp(timestamp, userId)

      // 2. 通知父组件开始聊天
      await onStartChat(newSessionId, suggestion)

      setIsSending(false)
    } catch (error) {
      console.error('创建会话失败:', error)
      setIsSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        return
      } else {
        e.preventDefault()
        handleSendMessage()
      }
    }
  }

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-8">
      {/* 欢迎内容 */}
      <div className="text-center max-w-2xl mb-8">
        {/* 智能体图标 */}
        <div className="w-20 h-20 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg overflow-hidden border border-blue-200">
          <img
            src={agentDetail?.icon || AIAvatarPlain}
            alt={agentDetail?.name || 'YouKnow AI'}
            className="w-12 h-12 object-cover"
            onError={(e) => {
              // 如果图片加载失败，使用默认头像
              e.currentTarget.src =
                'https://api.dicebear.com/7.x/avataaars/svg?seed=AI'
            }}
          />
        </div>

        {/* 欢迎文案 */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          我是 {agentDetail?.name || 'YouKnow'}
        </h1>
        <p className="text-lg text-gray-600 leading-relaxed mb-6">
          {agentDetail?.greeting ||
            '很高兴见到你！我可以帮你处理文件，写各种创意内容，请把你的任务交给我吧~'}
        </p>

        {/* 提示问题列表 */}
        {agentDetail?.questionSuggestionList &&
          agentDetail.questionSuggestionList.length > 0 && (
            <div className="space-y-2">
              <div className="flex flex-col gap-3">
                {agentDetail.questionSuggestionList.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    disabled={isSending || !isSearchConfigReady}
                    className="px-3 py-1.5 text-sm text-gray-700 bg-white hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-left flex items-center gap-2"
                  >
                    <Icon name="starSmall" size={10} />
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
      </div>

      {/* 输入区域 */}
      <div className="w-full max-w-2xl">
        <div className="relative">
          <div className="relative flex items-end bg-white border border-gray-200 rounded-3xl shadow-sm hover:shadow-md transition-shadow duration-200 focus-within:border-gray-300">
            {/* 文本输入区域 */}
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                !isSearchConfigReady
                  ? '正在加载知识库配置...'
                  : '告诉我你需要什么帮助...'
              }
              disabled={isSending || !isSearchConfigReady}
              rows={1}
              className="flex-1 resize-none border-0 bg-transparent py-4 px-4 text-base placeholder-gray-500 focus:outline-none min-h-[24px] max-h-[200px] overflow-y-auto"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            />

            {/* 发送按钮 */}
            <div className="flex-shrink-0 p-2">
              <button
                onClick={handleSendMessage}
                disabled={
                  !inputValue.trim() || isSending || !isSearchConfigReady
                }
                className="flex items-center justify-center w-10 h-10 text-white disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg disabled:hover:shadow-none"
                style={{
                  borderRadius: '31px',
                  background:
                    !inputValue.trim() || isSending || !isSearchConfigReady
                      ? '#d1d5db'
                      : 'linear-gradient(270deg, #5239E0 0.29%, #9684FF 101.29%)',
                }}
                title="发送"
              >
                <ArrowUp size={18} />
              </button>
            </div>
          </div>

          {/* 底部提示 */}
          <div className="mt-3 text-xs text-gray-500 text-center">
            {isSending
              ? '正在创建会话...'
              : !isSearchConfigReady
              ? '正在加载知识库配置...'
              : 'AI可能会犯错。请核实重要信息。'}
          </div>
        </div>
      </div>
    </div>
  )
}

export default WelcomePage
